import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import { Link, useLocation } from 'react-router-dom';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';

export default function Header() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const open = Boolean(anchorEl);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Detect scroll to add shadow and shrink effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Programs', path: '/programs' },
    { label: 'Transparency', path: '/transparency' },
    { label: 'Contact', path: '/contact' },
    { label: 'Gallery', path: '/gallery' },
    { label: 'Newsroom', path: '/newsroom' },
    { label: 'My Donations', path: '/my-donations' }
  ];

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={scrolled ? 4 : 0} 
        sx={{ 
          background: scrolled ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(12px)', // Premium glass effect
          borderBottom: scrolled ? 'none' : '1px solid rgba(0,0,0,0.05)',
          color: '#222', 
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: scrolled ? 64 : 72, sm: scrolled ? 64 : 80 }, transition: 'min-height 0.3s ease-in-out', width: '100%', minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
              <VolunteerActivismIcon sx={{ color: '#0b5fff', fontSize: 32, mr: 1 }} />
              <Typography variant="h5" component={Link} to="/" sx={{ color: '#0b5fff', textDecoration: 'none', fontWeight: 800, letterSpacing: 0.5 }}>
                ALC Trust
              </Typography>
            </Box>
            
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 1.5, alignItems: 'center' }}>
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Button 
                    key={item.label}
                    component={Link} 
                    to={item.path} 
                    sx={{ 
                      color: isActive ? '#0b5fff' : '#334155', 
                      fontWeight: isActive ? 700 : 600, 
                      textTransform: 'none',
                      position: 'relative',
                      fontSize: '1rem',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        width: isActive ? '100%' : '0%',
                        height: '2px',
                        bottom: 0,
                        left: 0,
                        backgroundColor: '#0b5fff',
                        transition: 'width 0.3s ease-in-out'
                      },
                      '&:hover::after': { width: '100%' },
                      '&:hover': { bgcolor: 'transparent', color: '#0b5fff' }
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
              
              <Button 
                component={Link} 
                to="/donate" 
                sx={{ 
                  bgcolor: '#0b5fff', 
                  color: '#fff', 
                  fontWeight: 700, 
                  borderRadius: 50, 
                  px: 4, 
                  py: 1, 
                  ml: 2, 
                  textTransform: 'none', 
                  boxShadow: '0 4px 14px 0 rgba(11, 95, 255, 0.39)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': { 
                    bgcolor: '#004ee6', 
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(11, 95, 255, 0.23)'
                  } 
                }}
              >
                Donate
              </Button>
            </Box>

            <Box sx={{ display: { xs: 'flex', lg: 'none' }, flexShrink: 0 }}>
              <IconButton color="inherit" onClick={handleOpen} aria-label="menu" sx={{ color: '#0b5fff' }}>
                <MenuIcon />
              </IconButton>
              <Menu 
                anchorEl={anchorEl} 
                open={open} 
                onClose={handleClose}
                PaperProps={{
                  sx: { mt: 1.5, borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }
                }}
              >
                {navItems.map((item) => (
                  <MenuItem 
                    key={item.label} 
                    component={Link} 
                    to={item.path} 
                    onClick={handleClose}
                    sx={{ color: location.pathname === item.path ? '#0b5fff' : '#222', fontWeight: location.pathname === item.path ? 700 : 500 }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
                <MenuItem component={Link} to="/donate" onClick={handleClose} sx={{ color: '#0b5fff', fontWeight: 700 }}>
                  Donate
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      {/* Spacer to push content down below the fixed AppBar */}
      <Toolbar sx={{ minHeight: { xs: 72, sm: 80 } }} />
    </>
  );
}
